/**
 * Draws the camera bubble into the screen recording (optionally cropped to an area).
 *
 * The camera is composited into the video itself, so it's recorded the same way in every
 * mode (full screen, window, tab, area) — it doesn't depend on a bubble being visible on the
 * captured screen.
 *
 * Frames are produced by a Worker timer at a steady rate: screen captures only deliver frames
 * when something changes, so a frame-driven pipeline would freeze the camera on a static
 * screen. Worker timers keep running while the AI Recording tab is in the background.
 *
 * - Chrome/Edge: MediaStreamTrackProcessor keeps the latest screen and camera frames; each tick
 *   draws them on an OffscreenCanvas and writes a VideoFrame to a MediaStreamTrackGenerator.
 * - Other browsers: hidden <video> elements + canvas.captureStream().
 */
import type { CropArea } from './cropPipeline';

export type CameraShape = 'circle' | 'square';
export type CameraSize = 'S' | 'M' | 'L';
export type CameraCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface CameraBubble {
  shape: CameraShape;
  size: CameraSize;
  corner: CameraCorner;
}

export const DEFAULT_CAMERA_BUBBLE: CameraBubble = { shape: 'circle', size: 'M', corner: 'bottom-right' };

/** Guards preferences restored from localStorage. */
export function isCameraBubble(value: unknown): value is CameraBubble {
  const v = value as Partial<CameraBubble> | null;
  return (
    !!v &&
    (v.shape === 'circle' || v.shape === 'square') &&
    (v.size === 'S' || v.size === 'M' || v.size === 'L') &&
    (v.corner === 'top-left' || v.corner === 'top-right' || v.corner === 'bottom-left' || v.corner === 'bottom-right')
  );
}

export interface Compositor {
  /** Video-only stream: screen (or area) with the camera bubble. */
  stream: MediaStream;
  /** Show/hide the bubble in the recording without stopping the camera. */
  setCameraVisible: (visible: boolean) => void;
  stop: () => void;
}

const FPS = 30;
/** Bubble diameter as a share of the output's shorter side. */
const SIZE_RATIO: Record<CameraSize, number> = { S: 0.18, M: 0.24, L: 0.32 };

const even = (n: number) => Math.max(2, Math.floor(n / 2) * 2);

function bubbleRect(outW: number, outH: number, bubble: CameraBubble) {
  const short = Math.min(outW, outH);
  const d = Math.round(short * SIZE_RATIO[bubble.size]);
  const margin = Math.round(short * 0.03);
  const x = bubble.corner.endsWith('right') ? outW - d - margin : margin;
  const y = bubble.corner.startsWith('bottom') ? outH - d - margin : margin;
  return { x, y, d };
}

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function bubblePath(ctx: Ctx2D, x: number, y: number, d: number, shape: CameraShape) {
  ctx.beginPath();
  if (shape === 'circle') {
    ctx.arc(x + d / 2, y + d / 2, d / 2, 0, Math.PI * 2);
  } else if (typeof (ctx as CanvasRenderingContext2D).roundRect === 'function') {
    (ctx as CanvasRenderingContext2D).roundRect(x, y, d, d, d * 0.14);
  } else {
    ctx.rect(x, y, d, d);
  }
  ctx.closePath();
}

/** Camera image cover-cropped to a square, clipped to the shape, with a light border. */
function drawBubble(
  ctx: Ctx2D,
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  outW: number,
  outH: number,
  bubble: CameraBubble,
) {
  if (!srcW || !srcH) return;
  const { x, y, d } = bubbleRect(outW, outH, bubble);
  const side = Math.min(srcW, srcH);
  const sx = (srcW - side) / 2;
  const sy = (srcH - side) / 2;

  ctx.save();
  bubblePath(ctx, x, y, d, bubble.shape);
  ctx.clip();
  ctx.drawImage(source, sx, sy, side, side, x, y, d, d);
  ctx.restore();

  ctx.save();
  bubblePath(ctx, x, y, d, bubble.shape);
  ctx.lineWidth = Math.max(2, Math.round(d * 0.02));
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.stroke();
  ctx.restore();
}

/** Crop rect in source pixels (the whole frame when no area is given). */
function cropRect(area: CropArea | undefined, vw: number, vh: number) {
  if (!area) return { x: 0, y: 0, w: vw, h: vh };
  const x = Math.max(0, Math.min(area.x, vw - 1));
  const y = Math.max(0, Math.min(area.y, vh - 1));
  return { x, y, w: Math.max(1, Math.min(area.width, vw - x)), h: Math.max(1, Math.min(area.height, vh - y)) };
}

/** Steady ticks from a Worker (not throttled like main-thread timers in background tabs). */
function createTicker(onTick: () => void) {
  const url = URL.createObjectURL(
    new Blob([`setInterval(() => postMessage(0), ${Math.round(1000 / FPS)});`], { type: 'text/javascript' }),
  );
  const worker = new Worker(url);
  worker.onmessage = onTick;
  return () => {
    worker.terminate();
    URL.revokeObjectURL(url);
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const w = window as any;
const hasInsertableStreams =
  typeof w.MediaStreamTrackProcessor === 'function' &&
  typeof w.MediaStreamTrackGenerator === 'function' &&
  typeof w.VideoFrame === 'function' &&
  typeof w.OffscreenCanvas === 'function';

export function createCompositor(
  screen: MediaStream,
  camera: MediaStream,
  options: { area?: CropArea; bubble: CameraBubble },
): Compositor {
  const screenTrack = screen.getVideoTracks()[0];
  const cameraTrack = camera.getVideoTracks()[0];
  if (!screenTrack || !cameraTrack) throw new Error('Screen and camera video tracks are required');
  return hasInsertableStreams
    ? frameCompositor(screenTrack, cameraTrack, options)
    : canvasCompositor(screenTrack, cameraTrack, options);
}

function frameCompositor(
  screenTrack: MediaStreamTrack,
  cameraTrack: MediaStreamTrack,
  { area, bubble }: { area?: CropArea; bubble: CameraBubble },
): Compositor {
  let stopped = false;
  let cameraVisible = true;
  let screenFrame: any = null;
  let cameraFrame: any = null;
  const readers: any[] = [];

  // Keep only the latest frame of each source.
  const pump = (track: MediaStreamTrack, keep: (frame: any) => void) => {
    const reader = new w.MediaStreamTrackProcessor({ track }).readable.getReader();
    readers.push(reader);
    (async () => {
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (stopped) { value.close(); break; }
          keep(value);
        }
      } catch { /* source ended or stopped */ }
    })();
  };
  pump(screenTrack, (f) => { screenFrame?.close(); screenFrame = f; });
  pump(cameraTrack, (f) => { cameraFrame?.close(); cameraFrame = f; });

  const generator = new w.MediaStreamTrackGenerator({ kind: 'video' });
  const writer = generator.writable.getWriter();
  let frameErrorLogged = false;
  let canvas: OffscreenCanvas | null = null;
  let ctx: OffscreenCanvasRenderingContext2D | null = null;
  let outW = 0;
  let outH = 0;

  const stopTicker = createTicker(() => {
    if (stopped || !screenFrame) return;
    // Encoder behind: skip this tick instead of queueing frames.
    if (writer.desiredSize !== null && writer.desiredSize <= 0) return;
    try {
      const vw = screenFrame.displayWidth;
      const vh = screenFrame.displayHeight;
      const crop = cropRect(area, vw, vh);
      if (!canvas) {
        // Output size is fixed on the first frame (the encoder expects a stable size).
        outW = even(crop.w);
        outH = even(crop.h);
        canvas = new OffscreenCanvas(outW, outH);
        ctx = canvas.getContext('2d');
      }
      if (!ctx) return;
      ctx.drawImage(screenFrame, crop.x, crop.y, crop.w, crop.h, 0, 0, outW, outH);
      if (cameraVisible && cameraFrame) {
        drawBubble(ctx, cameraFrame, cameraFrame.displayWidth, cameraFrame.displayHeight, outW, outH, bubble);
      }
      const frame = new w.VideoFrame(canvas, { timestamp: Math.round(performance.now() * 1000) });
      // The generator takes ownership of written frames.
      writer.write(frame).catch(() => frame.close());
    } catch (err) {
      // A frame can fail now and then; report once, not 30 times a second.
      if (!frameErrorLogged) {
        frameErrorLogged = true;
        console.error('[compositor] frame skipped:', err);
      }
    }
  });

  return {
    stream: new MediaStream([generator]),
    setCameraVisible: (visible) => { cameraVisible = visible; },
    stop: () => {
      if (stopped) return;
      stopped = true;
      stopTicker();
      readers.forEach((r) => r.cancel().catch(() => {}));
      screenFrame?.close();
      cameraFrame?.close();
      screenFrame = cameraFrame = null;
      writer.close().catch(() => {});
      try { generator.stop(); } catch { /* already stopped */ }
    },
  };
}

function canvasCompositor(
  screenTrack: MediaStreamTrack,
  cameraTrack: MediaStreamTrack,
  { area, bubble }: { area?: CropArea; bubble: CameraBubble },
): Compositor {
  let cameraVisible = true;
  const hiddenVideo = (track: MediaStreamTrack) => {
    const v = document.createElement('video');
    v.srcObject = new MediaStream([track]);
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', 'true');
    v.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(v);
    v.play().catch(() => { /* starts once data arrives */ });
    return v;
  };
  const screenVideo = hiddenVideo(screenTrack);
  const cameraVideo = hiddenVideo(cameraTrack);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  // Size the canvas now (from the track settings) rather than on the first drawn frame: the
  // recorder starts before that and would otherwise see the default 300x150 canvas.
  let sized = false;
  const settings = screenTrack.getSettings();
  if (settings.width && settings.height) {
    const crop = cropRect(area, settings.width, settings.height);
    canvas.width = even(crop.w);
    canvas.height = even(crop.h);
    sized = true;
  }

  const stopTicker = createTicker(() => {
    if (screenVideo.readyState < 2) return;
    const vw = screenVideo.videoWidth;
    const vh = screenVideo.videoHeight;
    if (!vw || !vh) return;
    const crop = cropRect(area, vw, vh);
    if (!sized) {
      canvas.width = even(crop.w);
      canvas.height = even(crop.h);
      sized = true;
    }
    ctx.drawImage(screenVideo, crop.x, crop.y, crop.w, crop.h, 0, 0, canvas.width, canvas.height);
    if (cameraVisible && cameraVideo.readyState >= 2) {
      drawBubble(ctx, cameraVideo, cameraVideo.videoWidth, cameraVideo.videoHeight, canvas.width, canvas.height, bubble);
    }
  });

  const stream = canvas.captureStream(FPS);
  return {
    stream,
    setCameraVisible: (visible) => { cameraVisible = visible; },
    stop: () => {
      stopTicker();
      stream.getTracks().forEach((t) => t.stop());
      [screenVideo, cameraVideo].forEach((v) => {
        v.pause();
        v.srcObject = null;
        v.remove();
      });
    },
  };
}
